import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

// ─── Types ───────────────────────────────────────────────────────────────────

interface FormField {
  key: string;
  label: string;
  type:
    | "text"
    | "email"
    | "phone"
    | "textarea"
    | "select"
    | "checkbox"
    | "date"
    | "number"
    | "multiselect"
    | "file";
  required: boolean;
  options?: string[];
  order: number;
}

interface FormDefinition {
  name: string;
  slug: string;
  description?: string;
  fields: FormField[];
}

interface FirmInfo {
  name: string;
  primaryColor?: string;
}

type FieldValue = string | boolean | string[];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const API_BASE = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");

function darkenColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.max(0, (num >> 16) - amount);
  const g = Math.max(0, ((num >> 8) & 0x00ff) - amount);
  const b = Math.max(0, (num & 0x0000ff) - amount);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function lightenColor(hex: string, alpha: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = num >> 16;
  const g = (num >> 8) & 0x00ff;
  const b = num & 0x0000ff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const INPUT_BASE =
  "w-full rounded-lg border px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 transition-all duration-150 outline-none";

// ─── Skeleton Loader ─────────────────────────────────────────────────────────

const SkeletonField: React.FC = () => (
  <div className="animate-pulse space-y-2">
    <div className="h-4 w-28 bg-gray-200 rounded" />
    <div className="h-11 w-full bg-gray-100 rounded-lg" />
  </div>
);

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-6 p-8">
    <div className="animate-pulse space-y-2">
      <div className="h-6 w-48 bg-gray-200 rounded" />
      <div className="h-4 w-72 bg-gray-100 rounded" />
    </div>
    <div className="space-y-5 pt-2">
      <SkeletonField />
      <SkeletonField />
      <SkeletonField />
      <SkeletonField />
    </div>
    <div className="h-12 w-full bg-gray-200 rounded-lg animate-pulse" />
  </div>
);

// ─── Success State ───────────────────────────────────────────────────────────

const SuccessView: React.FC<{ firmName: string; primaryColor: string }> = ({
  firmName,
  primaryColor,
}) => (
  <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
    <div
      className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
      style={{
        backgroundColor: lightenColor(primaryColor, 0.1),
        animation: "scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
      }}
    >
      <svg
        className="w-8 h-8"
        fill="none"
        viewBox="0 0 24 24"
        stroke={primaryColor}
        strokeWidth={2.5}
        style={{ animation: "drawCheck 0.5s ease-out 0.2s both" }}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </div>
    <h2 className="text-xl font-semibold text-gray-900 mb-2">
      Thank you for your submission
    </h2>
    <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
      {firmName} has received your information and will be in touch shortly.
    </p>
    <style>{`
      @keyframes scaleIn {
        0% { transform: scale(0); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes drawCheck {
        0% { stroke-dasharray: 30; stroke-dashoffset: 30; }
        100% { stroke-dasharray: 30; stroke-dashoffset: 0; }
      }
    `}</style>
  </div>
);

// ─── Error State ─────────────────────────────────────────────────────────────

const ErrorView: React.FC<{ message: string; onRetry?: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
    <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-5">
      <svg
        className="w-7 h-7 text-red-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
        />
      </svg>
    </div>
    <h2 className="text-lg font-semibold text-gray-900 mb-1">
      Something went wrong
    </h2>
    <p className="text-sm text-gray-500 mb-5">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="text-sm font-medium text-gray-700 hover:text-gray-900 underline underline-offset-2"
      >
        Try again
      </button>
    )}
  </div>
);

// ─── Checkmark Icon (shared) ─────────────────────────────────────────────────

const CheckIcon: React.FC = () => (
  <svg
    className="w-3 h-3 text-white"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={3}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

// ─── Field Input Component ───────────────────────────────────────────────────
// Extracted so each field can safely use its own useState for focus tracking.

interface FieldInputProps {
  field: FormField;
  value: FieldValue;
  hasError: boolean;
  primaryColor: string;
  onChange: (key: string, value: FieldValue) => void;
  onBlur: (key: string) => void;
  onToggleMultiselect: (key: string, option: string) => void;
}

const FieldInput: React.FC<FieldInputProps> = ({
  field,
  value,
  hasError,
  primaryColor,
  onChange,
  onBlur,
  onToggleMultiselect,
}) => {
  const [focused, setFocused] = useState(false);

  const borderClass = hasError
    ? "border-red-300"
    : "border-gray-200 hover:border-gray-300";

  const focusStyle: React.CSSProperties =
    focused && !hasError
      ? {
          boxShadow: `0 0 0 2px ${lightenColor(primaryColor, 0.2)}`,
          borderColor: primaryColor,
        }
      : {};

  const handleFocus = () => setFocused(true);
  const handleBlur = () => {
    setFocused(false);
    onBlur(field.key);
  };

  switch (field.type) {
    case "text":
    case "email":
    case "phone":
    case "number":
    case "date":
      return (
        <input
          type={field.type === "phone" ? "tel" : field.type}
          value={(value as string) || ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={
            field.type === "email"
              ? "you@example.com"
              : field.type === "phone"
              ? "(555) 123-4567"
              : ""
          }
          className={`${INPUT_BASE} ${borderClass}`}
          style={focusStyle}
        />
      );

    case "textarea":
      return (
        <textarea
          rows={4}
          value={(value as string) || ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Type your message..."
          className={`${INPUT_BASE} ${borderClass} resize-none`}
          style={focusStyle}
        />
      );

    case "select":
      return (
        <select
          value={(value as string) || ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={`${INPUT_BASE} ${borderClass} appearance-none bg-white bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px] bg-[right_12px_center] bg-no-repeat pr-10`}
          style={focusStyle}
        >
          <option value="">Select an option</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );

    case "multiselect":
      return (
        <div className="space-y-2">
          {(field.options || []).map((opt) => {
            const checked = ((value as string[]) || []).includes(opt);
            return (
              <label
                key={opt}
                className="flex items-center gap-3 cursor-pointer group"
              >
                <span
                  className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150"
                  style={{
                    borderColor: checked ? primaryColor : "#d1d5db",
                    backgroundColor: checked ? primaryColor : "transparent",
                  }}
                >
                  {checked && <CheckIcon />}
                </span>
                <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                  {opt}
                </span>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={checked}
                  onChange={() => onToggleMultiselect(field.key, opt)}
                />
              </label>
            );
          })}
        </div>
      );

    case "checkbox":
      return (
        <label className="flex items-center gap-3 cursor-pointer group">
          <button
            type="button"
            role="checkbox"
            aria-checked={!!value}
            onClick={() => onChange(field.key, !value)}
            className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150"
            style={{
              borderColor: value ? primaryColor : "#d1d5db",
              backgroundColor: value ? primaryColor : "transparent",
            }}
          >
            {value && <CheckIcon />}
          </button>
          <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
            {field.label}
          </span>
        </label>
      );

    default:
      return null;
  }
};

// ─── Main Component ──────────────────────────────────────────────────────────

const EmbedFormPage: React.FC = () => {
  const { firmSlug, formSlug } = useParams<{
    firmSlug: string;
    formSlug: string;
  }>();
  const [searchParams] = useSearchParams();

  // State
  const [form, setForm] = useState<FormDefinition | null>(null);
  const [firm, setFirm] = useState<FirmInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [btnHovered, setBtnHovered] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // Derive primary color: query param > firm setting > default
  const queryColor = searchParams.get("color");
  const primaryColor =
    queryColor && /^[0-9a-fA-F]{6}$/.test(queryColor)
      ? `#${queryColor}`
      : firm?.primaryColor || "#0F5132";
  const hoverColor = darkenColor(primaryColor, 25);

  // ─── postMessage resize observer ────────────────────────────────────────

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const iframeId = searchParams.get("id") || "kavoa-embed";

    const sendHeight = () => {
      window.parent.postMessage(
        {
          type: "kavoa-resize",
          height: document.body.scrollHeight,
          id: iframeId,
        },
        "*"
      );
    };

    const observer = new ResizeObserver(() => sendHeight());
    observer.observe(container);
    sendHeight();

    return () => observer.disconnect();
  }, [searchParams, form, submitted]);

  // ─── Fetch form definition ─────────────────────────────────────────────

  const fetchForm = useCallback(async () => {
    if (!firmSlug || !formSlug) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(
        `${API_BASE}/widget/${firmSlug}/forms/${formSlug}`
      );
      const formDef: FormDefinition = data.form;
      setForm({
        ...formDef,
        fields: [...formDef.fields].sort((a, b) => a.order - b.order),
      });
      setFirm(data.firm);

      // Initialize default values
      const defaults: Record<string, FieldValue> = {};
      formDef.fields.forEach((f) => {
        if (f.type === "checkbox") defaults[f.key] = false;
        else if (f.type === "multiselect") defaults[f.key] = [];
        else defaults[f.key] = "";
      });
      setValues(defaults);
    } catch (err: any) {
      const msg =
        err?.response?.status === 404
          ? "This form could not be found."
          : "Unable to load the form. Please try again later.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [firmSlug, formSlug]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  // ─── Field change handler ──────────────────────────────────────────────

  const handleChange = useCallback(
    (key: string, value: FieldValue) => {
      setValues((prev) => ({ ...prev, [key]: value }));
      setFieldErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    []
  );

  const handleBlur = useCallback((key: string) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  const toggleMultiselect = useCallback(
    (key: string, option: string) => {
      setValues((prev) => {
        const current = (prev[key] as string[]) || [];
        const next = current.includes(option)
          ? current.filter((o) => o !== option)
          : [...current, option];
        return { ...prev, [key]: next };
      });
      setFieldErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    []
  );

  // ─── Validation ────────────────────────────────────────────────────────

  const validate = (): boolean => {
    if (!form) return false;
    const errors: Record<string, string> = {};

    form.fields.forEach((field) => {
      if (field.type === "file") return;
      const val = values[field.key];

      if (field.required) {
        if (field.type === "checkbox" && val !== true) {
          errors[field.key] = "This field is required";
        } else if (field.type === "multiselect") {
          if (!Array.isArray(val) || val.length === 0) {
            errors[field.key] = "Please select at least one option";
          }
        } else if (
          typeof val === "string" &&
          val.trim() === "" &&
          field.type !== "checkbox"
        ) {
          errors[field.key] = "This field is required";
        }
      }

      if (
        field.type === "email" &&
        typeof val === "string" &&
        val.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
      ) {
        errors[field.key] = "Please enter a valid email address";
      }

      if (
        field.type === "phone" &&
        typeof val === "string" &&
        val.trim() &&
        !/^[+]?[\d\s()-]{7,20}$/.test(val)
      ) {
        errors[field.key] = "Please enter a valid phone number";
      }
    });

    setFieldErrors(errors);

    const allTouched: Record<string, boolean> = {};
    form.fields.forEach((f) => {
      allTouched[f.key] = true;
    });
    setTouched(allTouched);

    return Object.keys(errors).length === 0;
  };

  // ─── Submit handler ────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !form) return;

    setSubmitting(true);
    try {
      const responses = form.fields
        .filter((f) => f.type !== "file")
        .map((f) => ({ key: f.key, value: values[f.key] }));

      await axios.post(
        `${API_BASE}/widget/${firmSlug}/forms/${formSlug}`,
        { responses }
      );
      setSubmitted(true);
    } catch {
      setFieldErrors({ _form: "Failed to submit. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div
      ref={containerRef}
      className="min-h-screen flex items-start justify-center p-4"
      style={{
        backgroundColor: "transparent",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      }}
    >
      <div
        className="w-full max-w-lg bg-white overflow-hidden"
        style={{
          borderRadius: 16,
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
        }}
      >
        {/* Loading */}
        {loading && <LoadingSkeleton />}

        {/* Error */}
        {!loading && error && <ErrorView message={error} onRetry={fetchForm} />}

        {/* Success */}
        {!loading && !error && submitted && firm && (
          <SuccessView firmName={firm.name} primaryColor={primaryColor} />
        )}

        {/* Form */}
        {!loading && !error && !submitted && form && firm && (
          <form onSubmit={handleSubmit} noValidate>
            {/* Header */}
            <div className="px-8 pt-8 pb-2">
              <p
                className="text-xs font-semibold uppercase tracking-wider mb-1"
                style={{ color: primaryColor }}
              >
                {firm.name}
              </p>
              <h1 className="text-xl font-bold text-gray-900">{form.name}</h1>
              {form.description && (
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                  {form.description}
                </p>
              )}
            </div>

            {/* Fields */}
            <div className="px-8 py-6 space-y-5">
              {form.fields.map((field) => {
                if (field.type === "file") return null;
                const err = touched[field.key]
                  ? fieldErrors[field.key]
                  : undefined;

                return (
                  <div key={field.key}>
                    {field.type !== "checkbox" && (
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {field.label}
                        {field.required && (
                          <span className="text-red-400 ml-0.5">*</span>
                        )}
                      </label>
                    )}
                    <FieldInput
                      field={field}
                      value={values[field.key]}
                      hasError={!!err}
                      primaryColor={primaryColor}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onToggleMultiselect={toggleMultiselect}
                    />
                    {err && (
                      <p className="text-sm text-red-500 mt-1">{err}</p>
                    )}
                  </div>
                );
              })}

              {/* Global form error */}
              {fieldErrors._form && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
                  <p className="text-sm text-red-600">{fieldErrors._form}</p>
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="px-8 pb-8">
              <button
                type="submit"
                disabled={submitting}
                onMouseEnter={() => setBtnHovered(true)}
                onMouseLeave={() => setBtnHovered(false)}
                className="w-full py-3 text-sm font-semibold text-white rounded-lg transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  backgroundColor:
                    btnHovered && !submitting ? hoverColor : primaryColor,
                }}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EmbedFormPage;
